import React, { useState, useEffect, useCallback } from 'react';
import { Table, Card, Tag, Progress, Button, Spin } from 'antd';
import { EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  DashboardService,
  ProcessTypeSummary,
  ProcessItemsResponse,
} from '../../services/api/dashboardService';
import { ProcessItemsModal } from './ProcessItemsModal';
import './ProcessDetailTable.css';

interface ProcessDetailTableProps {
  processType: string;
  dateFrom: Date;
  dateTo: Date;
}

export const ProcessDetailTable: React.FC<ProcessDetailTableProps> = ({
  processType,
  dateFrom,
  dateTo,
}) => {
  const [processTypeSummary, setProcessTypeSummary] = useState<ProcessTypeSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedProcess, setSelectedProcess] = useState<string | null>(null);
  const [processItems, setProcessItems] = useState<ProcessItemsResponse | null>(null);
  const [itemsModalVisible, setItemsModalVisible] = useState(false);

  // Fetch process type summary
  const fetchProcessTypeSummary = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const summary = await DashboardService.getProcessTypeSummary(processType, dateFrom, dateTo);
      setProcessTypeSummary(summary);
    } catch (err) {
      console.error('Failed to fetch process type summary:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch process data');
    } finally {
      setLoading(false);
    }
  }, [processType, dateFrom, dateTo]);

  // Fetch process items for a specific process
  const fetchProcessItems = async (processId: string) => {
    try {
      const items = await DashboardService.getProcessItems(processId, dateFrom, dateTo);
      setProcessItems(items);
      setItemsModalVisible(true);
    } catch (err) {
      console.error('Failed to fetch process items:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch process items');
    }
  };

  useEffect(() => {
    void fetchProcessTypeSummary();
  }, [processType, dateFrom, dateTo, fetchProcessTypeSummary]);

  const handleViewItems = (processId: string) => {
    setSelectedProcess(processId);
    void fetchProcessItems(processId);
  };

  const handleRefresh = () => {
    void fetchProcessTypeSummary();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'constrained':
        return 'red';
      case 'active':
        return 'green';
      case 'underutilized':
        return 'orange';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: 'Process Name',
      dataIndex: 'process_name',
      key: 'process_name',
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: 'Artifact',
      dataIndex: 'artifact_name',
      key: 'artifact_name',
    },
    {
      title: 'Item Count',
      dataIndex: 'item_count',
      key: 'item_count',
      render: (count: number) => <span className="item-count">{count.toLocaleString()}</span>,
    },
    {
      title: 'Capacity Utilization',
      dataIndex: 'capacity_utilization',
      key: 'capacity_utilization',
      render: (utilization: number) => (
        <div className="utilization-cell">
          <Progress
            percent={utilization}
            size="small"
            status={utilization >= 90 ? 'exception' : utilization >= 70 ? 'active' : 'normal'}
            strokeColor={{
              '0%': '#108ee9',
              '100%': utilization >= 90 ? '#ff4d4f' : utilization >= 70 ? '#52c41a' : '#faad14',
            }}
          />
          <span className="utilization-text">{utilization.toFixed(1)}%</span>
        </div>
      ),
    },
    {
      title: 'Demand vs Capacity',
      dataIndex: 'demand_vs_capacity',
      key: 'demand_vs_capacity',
      render: (difference: number) => (
        <span className={`demand-capacity ${difference > 0 ? 'over-capacity' : 'under-capacity'}`}>
          {difference > 0 ? '+' : ''}
          {difference.toFixed(1)}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status.charAt(0).toUpperCase() + status.slice(1)}</Tag>
      ),
    },
    {
      title: 'Last Transition',
      dataIndex: 'last_transition_date',
      key: 'last_transition_date',
      render: (date: string) => <span>{date ? new Date(date).toLocaleDateString() : 'N/A'}</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: { process_id: string }) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewItems(record.process_id)}
        >
          View Items
        </Button>
      ),
    },
  ];

  if (loading && !processTypeSummary) {
    return (
      <Card title={`${processType} Processes`}>
        <div className="table-loading">
          <Spin size="large" />
          <p>Loading process data...</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="process-detail-table">
      <Card
        title={`${processType} Processes`}
        extra={
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
            Refresh
          </Button>
        }
      >
        {error && (
          <div className="error-message">
            <p>Error: {error}</p>
          </div>
        )}

        {processTypeSummary && (
          <div className="summary-stats">
            <div className="stat-item">
              <span className="stat-label">Total Processes:</span>
              <span className="stat-value">{processTypeSummary.processes.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Total Items:</span>
              <span className="stat-value">{processTypeSummary.total_items.toLocaleString()}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Average Utilization:</span>
              <span className="stat-value">
                {processTypeSummary.average_utilization.toFixed(1)}%
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Bottlenecks:</span>
              <span className="stat-value bottleneck-count">
                {processTypeSummary.bottleneck_processes.length}
              </span>
            </div>
          </div>
        )}

        <Table
          columns={columns}
          dataSource={processTypeSummary?.processes || []}
          rowKey="process_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} processes`,
          }}
          loading={loading}
          className="process-table"
        />
      </Card>

      {/* Process Items Modal */}
      <ProcessItemsModal
        visible={itemsModalVisible}
        processItems={processItems}
        onClose={() => {
          setItemsModalVisible(false);
          setProcessItems(null);
          setSelectedProcess(null);
        }}
      />
    </div>
  );
};
